import { isOneOf } from '@tronche2tek/shared-types';

import { SCOPES } from './token';

/**
 * Why the API refused an authenticated caller.
 *
 * These are not credential problems. The token is valid and Entra vouched for
 * the person; the account simply is not usable here. That distinction decides
 * what the client does next: a bad token is worth discarding and replacing, a
 * refused account is not — asking Entra again returns the same valid token and
 * lands on the same refusal, which is the loop this exists to break.
 */
export const DENIAL_CODES = [
  'account_not_provisioned',
  'account_disabled',
] as const;

export type DenialCode = (typeof DENIAL_CODES)[number];

export function isDenialCode(value: unknown): value is DenialCode {
  return isOneOf(DENIAL_CODES, value);
}

/**
 * What to ask Entra for when the operator clicks sign in.
 *
 * Off the denial page, no `prompt`: a legitimate operator with a live Entra
 * session comes back without touching anything, which is what makes signing in
 * again cheap enough to require it.
 *
 * On the denial page the opposite is true. The account that just got refused is
 * usually the only session in the browser — a kiosk has exactly one — so Entra
 * would silently hand back the same one and the button would be a no-op loop.
 * `select_account` forces the picker, which is the only useful action left:
 * come back as somebody else.
 */
export function loginRequestFor(denied: DenialCode | undefined): {
  scopes: string[];
  prompt?: 'select_account';
} {
  return denied
    ? { scopes: SCOPES, prompt: 'select_account' }
    : { scopes: SCOPES };
}

/**
 * Strip a stale `denied` from a URL, or return null if there was none.
 *
 * MSAL returns to the URL the sign-in started from, so a refused operator who
 * signs in as somebody else lands back on `/login?denied=…` — now holding a
 * perfectly good session. The login guard honours `denied` and refuses to move
 * them on, stranding them on a refusal that no longer applies. A completed
 * sign-in is precisely what makes it no longer apply.
 */
export function withoutDenial(href: string): string | null {
  const url = new URL(href);
  if (!url.searchParams.has('denied')) {
    return null;
  }
  url.searchParams.delete('denied');
  return url.toString();
}
